import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FamlaServices } from '../services';
import { useAppSelector } from '../stores';
import { MappingAccessDenied, navigateToMapping } from '../utils/mappingAccess';

import { MappingAccessCreditsModal } from './MappingAccessCreditsModal';

type OpenMappingParams = {
    processId: string;
    mapId: string;
    analysisId?: string;
    newTab?: boolean;
};

export function useMappingAccessNavigation() {
    const navigate = useNavigate();
    const users = useAppSelector((state) => state.auth);
    const role = useAppSelector((state) => state.role.role);
    const modalId = `insufficientCreditsModal-${useId().replace(/:/g, '')}`;
    const modalRef = useRef<HTMLSpanElement>(null);
    const [insufficientCreditsInfo, setInsufficientCreditsInfo] = useState<MappingAccessDenied | null>(null);
    const [ownerEmail, setOwnerEmail] = useState<string | undefined>();

    const isAdmin = role === 'ADMIN' || role === 'admin';
    const isOwner = isAdmin;

    useEffect(() => {
        const loadOwnerEmail = async () => {
            if (isAdmin || !users.session?.company_id || !users.access_token) return;
            try {
                const data = await FamlaServices(
                    'api_host',
                    `/company/by/id/${users.session.company_id}`,
                    'GET',
                    '',
                    users.access_token,
                );
                if (data.status === 200 && data.body?.data?.owner?.email) {
                    setOwnerEmail(data.body.data.owner.email);
                }
            } catch {
                /* silent */
            }
        };
        void loadOwnerEmail();
    }, [isAdmin, users.session?.company_id, users.access_token]);

    useEffect(() => {
        if (insufficientCreditsInfo && modalRef.current) {
            setTimeout(() => modalRef.current?.click(), 100);
        }
    }, [insufficientCreditsInfo]);

    const openMapping = useCallback(
        async (params: OpenMappingParams): Promise<boolean> => {
            const result = await navigateToMapping({
                ...params,
                accessToken: users.access_token || '',
                navigate,
                newTab: params.newTab,
            });
            if (!result.success) {
                setInsufficientCreditsInfo(result.denied);
                return false;
            }
            return true;
        },
        [navigate, users.access_token],
    );

    const creditsModal = React.createElement(MappingAccessCreditsModal, {
        info: insufficientCreditsInfo,
        modalId,
        modalRef,
        onClose: () => setInsufficientCreditsInfo(null),
        isOwner,
        adminEmail: ownerEmail,
    });

    return { openMapping, creditsModal, insufficientCreditsInfo };
}
